import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SEOHead } from "@/features/shared/SEOHead";

const Sitemap = () => {
  const mainPages = [
    { name: "Home", url: "/", description: "Frayze Technologies Inc. - Digital Solutions for Canadian Businesses" },
    { name: "Start Here", url: "/start/", description: "Find where you're losing leads and the fix" },
    { name: "About Us", url: "/about/", description: "Our mission to automate Canadian businesses" },
    { name: "Pricing", url: "/pricing/", description: "Transparent pricing for all our services" },
    { name: "Contact", url: "/contact/", description: "Get in touch with our Canadian team" },
    { name: "Case Studies", url: "/case-studies/", description: "Client success stories and results" },
    { name: "FAQ", url: "/faq/", description: "Frequently asked questions" },
    { name: "Onboarding", url: "/onboarding/", description: "What to expect when you join Frayze" },
    { name: "Careers", url: "/careers/", description: "Join our team" }
  ];

  const growthSystems = [
    { name: "Website Launch Program", url: "/website-launch-program/", description: "Get a professional website and CRM core in 7 days" },
    { name: "AI Strategy & Rollout", url: "/solutions/ai-strategy-rollout/", description: "Discovery, phased AI roadmap, implementation, and rollout" },
    { name: "CRM Automation", url: "/solutions/crm-automation/", description: "Complete CRM setup and AI-powered automation" },
    { name: "Lead Generation", url: "/solutions/lead-generation-system/", description: "Google Ads, social media campaigns, and automated lead generation" },
    { name: "Websites", url: "/solutions/websites/", description: "Conversion-focused websites for lead-driven businesses" },
    { name: "Local SEO & Reputation", url: "/solutions/local-seo-reputation/", description: "Local search visibility and review management" },
    { name: "Ad Management", url: "/solutions/ad-management-scale/", description: "Scale your revenue with managed paid ads" }
  ];

  const industryPages = [
    { name: "Automotive", url: "/automotive/", description: "Dealerships, Service Centers, Marine, Power Sports" },
    { name: "Trades & Construction", url: "/industries/trades/", description: "Plumbers, HVAC, Roofers, Painters, Construction" }
  ];

  const resourcesPages = [
    { name: "Resources Overview", url: "/resources/", description: "All our digital marketing resources and guides" },
    { name: "CRM Growth Playbook", url: "/solutions/crm-automation/", description: "Step-by-step guide to building your growth engine" },
    { name: "AI Automation Guide", url: "/canada-ai-strategy-small-business/", description: "How Canadian businesses are using AI to scale" },
    { name: "Platform & Integrations", url: "/platform/", description: "Everything included in a Frayze system, plus native integrations" },
    { name: "Blog", url: "/blog/", description: "Latest CRM automation, AI, and Ontario growth insights" }
  ];

  const conversionPages = [
    { name: "Book Consultation", url: "/book-consultation/", description: "Free Strategy Session" },
    { name: "Digital Audit", url: "/digital-audit/", description: "Free 2-minute business automation audit" },
  ];

  const serviceAreas = [
    { name: "Thunder Bay", url: "/service-areas/thunder-bay/", description: "Digital solutions for Thunder Bay businesses" },
    { name: "Sudbury", url: "/service-areas/sudbury/", description: "Digital marketing services for Sudbury businesses" },
    { name: "Toronto", url: "/service-areas/toronto/", description: "Digital marketing services for Toronto businesses" },
    { name: "Service Areas Overview", url: "/service-areas/", description: "All locations we serve" }
  ];

  const legalPages = [
    { name: "Privacy Policy", url: "/privacy-policy/", description: "How we protect your data" },
    { name: "Terms & Conditions", url: "/terms/", description: "Terms of service and usage" },
    { name: "Security", url: "/security/", description: "Security & data protection policy" },
    { name: "Acceptable Use", url: "/acceptable-use/", description: "Acceptable use policy" },
    { name: "Usage Billing", url: "/usage-billing/", description: "Understanding usage credits and billing" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <SEOHead
        title="Sitemap | Frayze Technologies Inc. | Find All Our Pages"
        description="Complete sitemap of all pages on Frayze.ca. Find our CRM solutions, lead generation services, web development, AI solutions, and more."
        keywords="sitemap, frayze technologies, CRM solutions, lead generation, web development, AI solutions, digital agency"
        ogTitle="Sitemap | Frayze Technologies Inc."
        ogDescription="Complete sitemap of all pages on Frayze.ca. Find our CRM solutions, lead generation services, web development, AI solutions, and more."
        twitterTitle="Sitemap | Frayze Technologies Inc."
        twitterDescription="Complete sitemap of all pages on Frayze.ca. Find our CRM solutions, lead generation services, web development, AI solutions, and more."
        canonical="https://frayze.ca/sitemap/"
        robots="noindex, follow"
      />

      <main className="pt-40 md:pt-40">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-subtle">
          <div className="container mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Site <span className="bg-gradient-cta bg-clip-text text-transparent">Map</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Find all pages and resources on Frayze.ca. Navigate to our CRM solutions,
              lead generation services, web development, AI solutions, and more.
            </p>
          </div>
        </section>

        {/* Sitemap Content */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">

              {/* Core Pages */}
              <Card>
                <CardHeader>
                  <CardTitle>Core Pages</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {mainPages.map((page) => (
                      <li key={page.url}>
                        <a
                          href={page.url}
                          className="block p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                        >
                          <div className="font-medium text-primary group-hover:text-accent transition-colors">
                            {page.name}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {page.description}
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Growth Systems */}
              <Card>
                <CardHeader>
                  <CardTitle>Growth Systems</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {growthSystems.map((page) => (
                      <li key={page.url}>
                        <a
                          href={page.url}
                          className="block p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                        >
                          <div className="font-medium text-primary group-hover:text-accent transition-colors">
                            {page.name}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {page.description}
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Industries */}
              <Card>
                <CardHeader>
                  <CardTitle>Industries</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {industryPages.map((page) => (
                      <li key={page.url}>
                        <a
                          href={page.url}
                          className="block p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                        >
                          <div className="font-medium text-primary group-hover:text-accent transition-colors">
                            {page.name}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {page.description}
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Resources */}
              <Card>
                <CardHeader>
                  <CardTitle>Resources</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {resourcesPages.map((page) => (
                      <li key={page.url}>
                        <a
                          href={page.url}
                          className="block p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                        >
                          <div className="font-medium text-primary group-hover:text-accent transition-colors">
                            {page.name}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {page.description}
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Get Started */}
              <Card>
                <CardHeader>
                  <CardTitle>Get Started</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {conversionPages.map((page) => (
                      <li key={page.url}>
                        <a
                          href={page.url}
                          className="block p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                        >
                          <div className="font-medium text-primary group-hover:text-accent transition-colors">
                            {page.name}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {page.description}
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Service Areas */}
              <Card>
                <CardHeader>
                  <CardTitle>Service Areas</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {serviceAreas.map((page) => (
                      <li key={page.url}>
                        <a
                          href={page.url}
                          className="block p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                        >
                          <div className="font-medium text-primary group-hover:text-accent transition-colors">
                            {page.name}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {page.description}
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Case Studies */}
              <Card>
                <CardHeader>
                  <CardTitle>Case Studies</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {[
                      { name: "All Case Studies", url: "/case-studies/", description: "Companies powered by Frayze" },
                      { name: "Need A Car", url: "/case-studies/need-a-car-automotive-growth/", description: "Multi-location automotive growth system" },
                      { name: "Global Aluminum Solutions", url: "/case-studies/global-aluminum-lead-generation/", description: "Contractor lead generation in the GTA" },
                      { name: "JC Training", url: "/case-studies/jc-training-professional-services/", description: "AI chatbot & outreach for sales coaching" },
                      { name: "South Core Roar", url: "/case-studies/south-core-roar-event-platform/", description: "Event platform & custom app" },
                      { name: "Centennial Family Foods", url: "/case-studies/centennial-family-foods-retail-system/", description: "Paper-flyer to multi-channel retail system" },
                    ].map((page) => (
                      <li key={page.url}>
                        <a
                          href={page.url}
                          className="block p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                        >
                          <div className="font-medium text-primary group-hover:text-accent transition-colors">
                            {page.name}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {page.description}
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Legal */}
              <Card className="md:col-span-2 lg:col-span-3">
                <CardHeader>
                  <CardTitle>Legal & Support</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {legalPages.map((page) => (
                      <li key={page.url}>
                        <a
                          href={page.url}
                          className="block p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                        >
                          <div className="font-medium text-primary group-hover:text-accent transition-colors">
                            {page.name}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {page.description}
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

            </div>

            {/* Additional Info */}
            <div className="mt-12 text-center max-w-4xl mx-auto">
              <Card className="bg-muted/20">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold mb-4">XML Sitemaps</h3>
                  <p className="text-muted-foreground mb-4">
                    For search engines and developers, you can also access our XML sitemaps:
                  </p>
                  <div className="space-y-2">
                    <a
                      href="/sitemap_index.xml"
                      className="block text-primary hover:text-accent transition-colors font-medium"
                    >
                      https://frayze.ca/sitemap_index.xml (XML Sitemap Index)
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Sitemap;
